import defer from "lodash.defer";

export type IntervalConfig = {
	milliseconds: number;
	count: number;
};

export type PollIntervalConfig = IntervalConfig[];

class Poller {
	private intervalRef;
	private readonly intervalStack: number[];

	constructor(intervalStack: number[]) {
		this.intervalStack = intervalStack;
	}

	private getNextInterval(): number {
		if (!this.intervalStack.length) return undefined;
		return this.intervalStack.shift();
	}

	public pollNext(doPoll) {
		const nextInterval: number = this.getNextInterval();
		if (!nextInterval) {
			console.log("Poller.pollNext() Max attempts reached.");
			this.clear();
			return;
		}
		this.intervalRef = setTimeout(doPoll, nextInterval);
	}

	public clear() {
		clearTimeout(this.intervalRef);
	}
}

export default abstract class BasePoller<T, P> {
	protected pollers: { [key: string]: Poller } = {};

	// TODO: later - cancel polling on logout
	public async startPolling(key: string, params?: P, initialData?: T) {
		console.log("BasePoller.startPolling() params:", params);
		if (initialData && !this.keepPolling(initialData)) {
			return;
		}

		this.stopPolling(key); // cancel previous interval
		this.pollers = this.pollers || {};

		// create stack of interval executions
		const intervalConfig: PollIntervalConfig = this.getPollIntervalConfig();
		const stack: number[] = [];
		intervalConfig.forEach((config: IntervalConfig) => {
			for (let i = 0; i < config.count; i++) {
				stack.push(config.milliseconds);
			}
		});

		const poller: Poller = new Poller(stack);
		this.pollers[key] = poller;
		this.doPoll(key, params);
	}

	private doPoll(key: string, params?: P) {
		this.pollers[key].clear();
		const callback = () => this.doPoll(key, params);

		defer(() =>
			this.getData(params)
				.then((data: T) => {
					if (this.keepPolling(data)) {
						this.pollers[key].pollNext(callback);
					}
				})
				.catch((err) => {
					// execute cycle whether succeeds or fails
					this.pollers[key].pollNext(callback);
				})
		);
	}

	public stopPolling(key: string) {
		console.log("BasePoller.stopPolling() key:", key);
		const poller: Poller = this.pollers[key];
		if (!poller) return;
		poller.clear();
		this.pollers[key] = undefined;
	}

	abstract getPollIntervalConfig(): PollIntervalConfig;
	abstract getData(params?: P): Promise<T>;
	abstract keepPolling(data: T): boolean;
}
